/* eslint-disable react/prop-types */
import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";
import { toast } from "react-toastify";
import { createAutoQuote, getQuotationDetails, uploadPrescription, validatePrescription } from "./api";
import { LINE_STATUS, STEPS } from "./constants";
import { buildAddedLine, buildLines, buildMedicineItems, reconcile, resolveLine, suggestedQuantity } from "./helpers";
import MedicinesStep from "./components/MedicinesStep";
import PatientStep from "./components/PatientStep";
import PrescriptionStep from "./components/PrescriptionStep";
import QuoteStep from "./components/QuoteStep";
import RxViewer from "./components/RxViewer";
import StepLedger from "./components/StepLedger";

/**
 * Create a quotation from a prescription photo.
 *
 * Find the patient, upload the prescription, check what the machine read, and
 * commit it. The prescription stays on screen through the review because the
 * pharmacist's job here is verification, not data entry.
 */
const CreateQuotation = () => {
    const navigate = useNavigate();

    const [step, setStep] = useState(STEPS.PATIENT);
    const [patient, setPatient] = useState(null);
    const [files, setFiles] = useState([]);
    const [links, setLinks] = useState([]);
    const [activePage, setActivePage] = useState(0);
    const [prescriptionId, setPrescriptionId] = useState(null);
    const [lines, setLines] = useState([]);
    const [activeLineId, setActiveLineId] = useState(null);
    const [isReading, setIsReading] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [result, setResult] = useState(null);

    // Monotonic, so ids stay unique after lines are removed — `lines.length`
    // would hand out an id that a deleted line already used.
    const addedCount = useRef(0);

    const activeLine = lines.find((line) => line.id === activeLineId);

    const ledgerValues = useMemo(() => {
        const ready = lines.filter((line) => line.included && line.status === LINE_STATUS.MATCHED).length;

        return {
            [STEPS.PATIENT]: patient ? `${patient.patientName} · ${patient.user.contact_no}` : "",
            [STEPS.PRESCRIPTION]: links.length
                ? `${links.length} ${links.length === 1 ? "page" : "pages"}`
                : "",
            [STEPS.MEDICINES]: lines.length ? `${ready} of ${lines.length} ready` : "",
            [STEPS.QUOTE]: result ? `#${result.quotationId}` : "",
        };
    }, [patient, links, lines, result]);

    const readPrescription = async () => {
        setIsReading(true);

        try {
            const uploaded = await uploadPrescription(files);
            if (!uploaded.length) throw new Error("The upload came back empty");

            const data = await validatePrescription({
                links: uploaded.map((file) => file.link),
                userMappingId: patient.userMappingId,
            });

            // validate() echoes back signed links to the stored prescription —
            // prefer those over the upload response, they're what OCR actually read.
            const viewable = data.prescriptionLink?.length
                ? data.prescriptionLink
                : uploaded.map((file) => file.url);

            const parsed = buildLines(data, viewable[0]);
            if (!parsed.length) {
                toast.error("No medicines could be read from this prescription");
                setIsReading(false);
                return;
            }

            setLinks(viewable);
            setPrescriptionId(data.prescriptionId ?? data.ocrPrescriptionId ?? null);
            setLines(parsed);
            setActiveLineId(parsed[0].id);
            setStep(STEPS.MEDICINES);
        } catch (error) {
            toast.error(error?.message || "Couldn't read the prescription");
        } finally {
            setIsReading(false);
        }
    };

    const changeLine = (nextLine) =>
        setLines((prev) => prev.map((line) => (line.id === nextLine.id ? nextLine : line)));

    const addLine = (pick) => {
        addedCount.current += 1;
        const line = buildAddedLine(pick, addedCount.current);
        setLines((prev) => [...prev, line]);
        setActiveLineId(line.id);
    };

    const removeLine = (lineId) => {
        const next = lines.filter((line) => line.id !== lineId);
        setLines(next);
        // Selection drives the prescription highlight, so hand it to a line that
        // still exists.
        if (lineId === activeLineId) setActiveLineId(next[0]?.id ?? null);
    };

    const undoRemoveLine = (line, originalIdx) => {
        setLines((prev) => {
            const next = [...prev];
            next.splice(originalIdx, 0, line);
            return next;
        });
        setActiveLineId(line.id);
    };

    const scrollToPrescription = () => window.scrollTo({ top: 0, behavior: "smooth" });

    const resolveLineById = (lineId, pick) =>
        setLines((prev) =>
            prev.map((line) => {
                if (line.id !== lineId) return line;
                const resolved = resolveLine(line, pick);
                return { ...resolved, quantity: suggestedQuantity(resolved.routine) };
            })
        );

    const createQuotation = async () => {
        const medicineItems = buildMedicineItems(lines);

        if (!medicineItems.length) {
            toast.error("Match at least one medicine first");
            return;
        }

        setIsCreating(true);

        try {
            const created = await createAutoQuote({
                userId: patient.user.id,
                prescriptionId,
                medicineItems,
            });

            const sentLines = lines.filter((line) => line.included && line.status === LINE_STATUS.MATCHED);

            let reconciliation = { landed: sentLines, dropped: [], unknown: true };
            try {
                const quotation = await getQuotationDetails(created.quotationId);
                reconciliation = reconcile(sentLines, quotation);
            } catch {
                // Reconciliation is best-effort — the quotation exists either way.
            }

            setResult({ quotationId: created.quotationId, sentLines, reconciliation });
            setStep(STEPS.QUOTE);
        } catch (error) {
            toast.error(error?.message || "Couldn't create the quotation");
        } finally {
            setIsCreating(false);
        }
    };

    const startAnother = () => {
        setStep(STEPS.PATIENT);
        setPatient(null);
        setFiles([]);
        setLinks([]);
        setActivePage(0);
        setPrescriptionId(null);
        setLines([]);
        setActiveLineId(null);
        setResult(null);
        addedCount.current = 0;
    };

    const isSplit = step === STEPS.MEDICINES && links.length > 0;

    return (
        <div className="min-h-screen bg-cod-gray-50 pb-10">
            <header className="border-b border-cod-gray-100 bg-white-50 px-5 pb-5 pt-4 sm:px-8">
                <button
                    type="button"
                    onClick={() => navigate("/quotations")}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-cod-gray-400 transition-colors hover:text-cod-gray-900"
                >
                    <FiArrowLeft size={13} />
                    Quotations
                </button>

                <h1 className="mt-2 text-2xl font-semibold tracking-tight text-cod-gray-900">
                    Create a quotation
                </h1>

                <div className="mt-5">
                    <StepLedger current={step} values={ledgerValues} onStepBack={setStep} />
                </div>
            </header>

            <main className={`px-5 sm:px-8 ${isSplit ? "pt-8" : "pb-8 pt-12"}`}>
                {isSplit ? (
                    <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]">
                        <div className="lg:sticky lg:top-6 lg:self-start">
                            <RxViewer
                                pages={links}
                                activePage={activePage}
                                onPageChange={setActivePage}
                                boundingBox={activeLine?.boundingBox}
                            />
                            {patient && (
                                <div className="mt-4 rounded-lg border border-cod-gray-100 bg-white-50 p-4">
                                    <p className="text-[15px] font-semibold text-cod-gray-900">{patient.patientName}</p>
                                    <p className="mt-0.5 text-xs tabular-nums text-cod-gray-400">
                                        {patient.user.contact_no}
                                    </p>
                                </div>
                            )}
                        </div>

                        <MedicinesStep
                            lines={lines}
                            activeLineId={activeLineId}
                            onSelectLine={setActiveLineId}
                            onChangeLine={changeLine}
                            onResolveLine={resolveLineById}
                            onAddLine={addLine}
                            onRemoveLine={removeLine}
                            onUndoRemoveLine={undoRemoveLine}
                            onCreate={createQuotation}
                            isCreating={isCreating}
                            onScrollToPrescription={scrollToPrescription}
                        />
                    </div>
                ) : (
                    <>
                        {step === STEPS.PATIENT && (
                            <PatientStep
                                onSelect={(selection) => {
                                    setPatient(selection);
                                    setStep(STEPS.PRESCRIPTION);
                                }}
                            />
                        )}

                        {step === STEPS.PRESCRIPTION && (
                            <PrescriptionStep
                                files={files}
                                onFilesChange={setFiles}
                                onSubmit={readPrescription}
                                isReading={isReading}
                            />
                        )}

                        {step === STEPS.QUOTE && result && (
                            <QuoteStep
                                quotationId={result.quotationId}
                                sentLines={result.sentLines}
                                reconciliation={result.reconciliation}
                                patientName={patient?.patientName}
                                onStartAnother={startAnother}
                            />
                        )}
                    </>
                )}
            </main>
        </div>
    );
};

export default CreateQuotation;
